import { useApiGet } from '../../../../services/tanstackApiService';

// API enums
export enum InvoiceLogStatus {
  Paid = 'paid',
  Unpaid = 'unpaid',
  Draft = 'draft',
}

// Raw object coming from API
export interface InvoiceLogApiItem {
  history_id: number;
  history_date: string;               // e.g. "2025-12-17T09:06:25.012104Z"
  history_type: string;               // "+" | "~" | "-"
  history_type_display: string;       // "Created" | "Updated" | "Deleted"
  user_name: string;
  bill_number: string | null;
  visit_date: string;
  status: InvoiceLogStatus;
  bill_type: string | null;
  invoice_patient_type: string;
  total_amount: string;
  concession_amount: string;
  tax_amount: string;
  net_amount: string;
  paid_amount: string;
  balance_amount: string;
}

// Meta info from API
export interface InvoiceLogMeta {
  total_count: number;
  limit: number;
  offset: number;
  next: string | null;
  previous: string | null;
}

// Entire API response
export interface InvoiceLogApiResponse {
  objects: InvoiceLogApiItem[];
  meta: InvoiceLogMeta;
  invoice_id: number;
  bill_number: string | null;
  total_changes: number;
}

// ---------------------------------------------------------------------
// Reusable helpers for Invoice Logs
// ---------------------------------------------------------------------

// Build a stable query key for invoice logs
export const invoiceLogsKey = (
  invoiceId: number | string,
  page: number,
  limit: number,
  search?: string
) => ['invoices', 'logs', invoiceId, { page, limit, search }] as const;

// Build the REST path for invoice logs
export const invoiceLogsPath = (invoiceId: number | string) =>
  `invoices/${invoiceId}/logs/`;

// Hook params
export interface UseInvoiceLogsParams {
  invoiceId: number | string | null;
  page: number;
  limit: number;
  search?: string;
  enabled?: boolean;
}

// React Query hook using useApiGet
export const useInvoiceLogsQuery = ({
  invoiceId,
  page,
  limit,
  search,
  enabled = true,
}: UseInvoiceLogsParams) => {
  const offset = (page - 1) * limit;

  return useApiGet<InvoiceLogApiResponse>(
    invoiceLogsKey(invoiceId ?? 0, page, limit, search),
    {
      path: invoiceLogsPath(invoiceId ?? 0),
      query: {
        limit,
        offset,
        search_query: search || undefined,
      },
    },
    {
      enabled: enabled && !!invoiceId,
    }
  );
};
